import { useRef } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Dialog } from "@mui/material";
import { GameCase } from "app/types";
import { RootState } from "app/store";
import { useUserConfig } from "app/hook";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useScale, usePlaySound } from "common/utils";
import { FadeTransition, CloseBtn } from "common/elements";
import { CheckMark } from "./assets";

const GameCaseArray = [
  {
    type: GameCase.GRAY,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_gray_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.SOFT_YELLOW,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_soft_yellow_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.GREEN,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_green_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.CYAN,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_cyan_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.BLUE,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_blue_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.MILK,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_milk_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.PINK,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_pink_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.ORANGE,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_orange_case.png`}
        className="w-full h-full"
      />
    ),
  },
  {
    type: GameCase.SOFT_PINK,
    icon: () => (
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/mini_soft_pink_case.png`}
        className="w-full h-full"
      />
    ),
  },
];

const MODAL_WIDTH = 1080;
const MODAL_HEIGHT = 760;

export const GameCaseStock = ({
  visible,
  gameCase,
  selectGameCase,
  close,
}: {
  visible: boolean;
  gameCase: GameCase;
  selectGameCase: (gameCase: GameCase) => void;
  close: (reset: boolean) => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleChangeUserConfig = useUserConfig();
  const config = useSelector((state: RootState) => state.config.userConfig);

  const handleSelected = (gameCase: GameCase) => () => {
    play();
    selectGameCase(gameCase);
  };

  const handleConfirm = () => {
    play();
    handleChangeUserConfig("game_case", gameCase);
    toast(
      <div>
        <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
      </div>,
    );
    setTimeout(() => close(true), 300);
  };

  const handleClose = () => {
    play();
    setTimeout(() => close(true), 300);
  };

  return (
    <Dialog
      open={visible}
      fullScreen
      onClose={handleClose}
      maxWidth={false}
      sx={{ zIndex: 999999 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
      }}
      PaperProps={{
        style: { margin: 0, overflow: "visible", backgroundColor: "transparent" },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center w-full h-full">
        <div
          className="flex-col-el flex-center relative"
          style={{
            width: MODAL_WIDTH * scale,
            height: MODAL_HEIGHT * scale,
          }}
        >
          <div
            className="flex-col-el shrink-0 border-solid border-white border-[8px] rounded-lg bg-beige-60 relative"
            style={{
              width: MODAL_WIDTH,
              height: MODAL_HEIGHT,
              transform: `scale(${scale})`,
              filter:
                "drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.3)) drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.3))",
            }}
          >
            <CloseBtn close={handleClose} />

            <div className="flex-col-el flex-center py-4">
              <p className="text-body text-textcolor-black text-[24px]">
                {I18n.t("MSG_USER_PROFILE_CASE_BTN")}
              </p>
            </div>

            <div className="flex-col-el flex-center flex-1 px-6 pt-6">
              <div className="grid grid-cols-3 gap-6 justify-items-center">
                {GameCaseArray.map((g, index) => {
                  const Icon = g.icon;
                  const isSelected = gameCase === g.type;
                  return (
                    <div className="flex-col-center relative" key={index}>
                      {config.game_case === g.type && (
                        <div className="flex-col-center !absolute top-[-16px] left-[0px] z-10">
                          <CheckMark />
                        </div>
                      )}
                      <div
                        onClick={handleSelected(g.type)}
                        className={`flex-col-center w-[280px] h-[140px] cursor-pointer rounded-lg bg-white active:scale-95 ${
                          isSelected
                            ? "border-solid border-[8px] border-green-100/40"
                            : "border-solid border-[2px] border-gray-20"
                        }`}
                      >
                        <div className="flex-col-center w-[240px] h-[120px]">
                          <Icon />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex-col-el flex-center py-4 alpha">
              <button
                onClick={handleConfirm}
                className="btn btn-primary"
                disabled={config.game_case === gameCase}
              >
                <div className="flex-row-center gap-1 mb-[3px]">
                  <span className="material-symbols-outlined !text-4xl text-white">check</span>
                  <p className="text-body text-white text-font-size-body1">
                    {I18n.t("MSG_CONFIRM_BTN")}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
